import { DateTime } from "luxon";
import { useTimeZone } from "../../OS/Timezone";
import { useWorkspaceTimeZone } from "../../OS/WorkspaceTimezone";

export type FormattedDateStyle = "DEFAULT" | "WITHOUT_TIME" | "TIME_ONLY";

type FormattedDateStyleExtended =
  | "DEFAULT"
  | "WITHOUT_TIME"
  | "TIME_ONLY"
  | "RELATIVE_CLOSE_TIME"
  | "RELATIVE_CLOSE_TIME_WITHOUT_TIME";

export function FormattedDateRaw(props: {
  dateISO: string;
  style?: FormattedDateStyle;
}) {
  return useFormattedDateRaw(props.dateISO, props.style ?? "DEFAULT");
}
export function FormattedDateRawInWorkspace(props: {
  dateISO: string;
  style?: FormattedDateStyle;
}) {
  return useFormattedDateRawInWorkSpaceTimeZone(
    props.dateISO,
    props.style ?? "DEFAULT"
  );
}

export function useFormattedDateRaw(
  dateISO: string | undefined,
  style: FormattedDateStyle
) {
  const timezone = useTimeZone();

  return useFormattedDateRawInTz(dateISO, style, timezone);
}
export function useFormattedDateRawInWorkSpaceTimeZone(
  dateISO: string | undefined,
  style: FormattedDateStyle
) {
  const timezone = useWorkspaceTimeZone();

  return useFormattedDateRawInTz(dateISO, style, timezone);
}
function useFormattedDateRawInTz(
  dateISO: string | undefined,
  style: FormattedDateStyle,
  timezone: string
) {
  if (!dateISO) return undefined;
  const date = DateTime.fromISO(dateISO, {
    zone: timezone,
  });

  const now = DateTime.now().setZone(timezone);
  const lessThanADay =
    now.plus({ day: -1 }).startOf("day") < date &&
    date < now.plus({ day: 1 }).startOf("day");

  const fStyleExtended = getFormatStyle(style, lessThanADay);

  return formatString(date, fStyleExtended);
}

function getFormatStyle(
  style: FormattedDateStyleExtended,
  isLessThanADay: boolean
) {
  if (isLessThanADay && style === "DEFAULT") {
    return "RELATIVE_CLOSE_TIME";
  }
  if (isLessThanADay && style === "WITHOUT_TIME") {
    return "RELATIVE_CLOSE_TIME_WITHOUT_TIME";
  }
  return style;
}

function formatString(date: DateTime, style: FormattedDateStyleExtended) {
  switch (style) {
    case "TIME_ONLY":
      return date.toLocaleString(DateTime.TIME_24_SIMPLE);

    case "WITHOUT_TIME":
      return date.toLocaleString({
        ...DateTime.DATE_MED_WITH_WEEKDAY,
        year: undefined,
      });
    case "RELATIVE_CLOSE_TIME":
      return (
        replaceSwedishSpaces(date.toRelativeCalendar()) +
        " " +
        date.toLocaleString(DateTime.TIME_24_SIMPLE)
      );
    case "RELATIVE_CLOSE_TIME_WITHOUT_TIME":
      return replaceSwedishSpaces(date.toRelativeCalendar());

    case "DEFAULT":
    default:
      return date.toLocaleString({
        ...DateTime.DATETIME_MED_WITH_WEEKDAY,
        year: undefined,
        hour: "2-digit",
      });
  }
}

function replaceSwedishSpaces(str: string | null) {
  return str
    ?.replace("i går", "igår")
    ?.replace("i morgon", "imorgon")
    ?.replace("i dag", "idag");
}
